/**
 * The pilot's logbook: easter eggs found, lifetime laps, fastest lap.
 * Persists to localStorage AND a cookie scoped to .free.vet, so when the
 * other subdomains (race. cad. print. mind.) exist, the logbook and the
 * drone-cursor unlock travel with the visitor across the whole site.
 * The domain cookie is silently ignored on localhost; the plain one covers
 * dev. No backend, nothing leaves the browser.
 */

export const EGGS = [
  { id: "race-flag", label: "Found the checkered flag" },
  { id: "konami", label: "Entered the old code" },
  { id: "encore", label: "Called an encore" },
] as const;

export type LogState = { eggs: string[]; laps: number; best: number | null };

const KEY = "fv_log";
const subs = new Set<(s: LogState) => void>();

function readCookie(): string | null {
  const m = document.cookie.match(/(?:^|;\s*)fv_log=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

function load(): LogState {
  try {
    const raw = localStorage.getItem(KEY) ?? readCookie();
    if (raw) {
      const p = JSON.parse(raw) as Partial<LogState>;
      return {
        eggs: Array.isArray(p.eggs) ? p.eggs.filter((e): e is string => typeof e === "string") : [],
        laps: typeof p.laps === "number" && isFinite(p.laps) ? p.laps : 0,
        best: typeof p.best === "number" && isFinite(p.best) ? p.best : null,
      };
    }
  } catch {
    // corrupted state: start a fresh logbook
  }
  return { eggs: [], laps: 0, best: null };
}

let state: LogState = load();

function persist() {
  const raw = JSON.stringify(state);
  try {
    localStorage.setItem(KEY, raw);
  } catch {
    // storage may be unavailable (private mode); cookie still tries below
  }
  const enc = encodeURIComponent(raw);
  const tail = "; path=/; max-age=34560000; SameSite=Lax";
  document.cookie = KEY + "=" + enc + tail;
  document.cookie = KEY + "=" + enc + "; domain=.free.vet" + tail;
  subs.forEach((f) => f(state));
}

export const logbook = {
  state: () => state,
  subscribe(f: (s: LogState) => void): () => void {
    subs.add(f);
    return () => {
      subs.delete(f);
    };
  },
  found(id: string) {
    if (state.eggs.includes(id)) return;
    state = { ...state, eggs: [...state.eggs, id] };
    persist();
  },
  addLap(ms: number) {
    state = {
      ...state,
      laps: state.laps + 1,
      best: state.best === null || ms < state.best ? ms : state.best,
    };
    persist();
  },
  /** Free's rule: the drone cursor goes site-wide past 5 lifetime laps. */
  cursorUnlocked: () => state.laps > 5,
};
